<script setup>
  import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { onAuthStateChange } from './firebase/auth'
  import { useAuth } from './composables/useAuth'
  import { useI18n } from 'vue-i18n'
  import appBar from './appBar.vue'
  import mainDrawer from './drawer.vue'
  import GlobalNotification from './components/shared/GlobalNotification.vue'
  import ImpersonationBanner from './components/common/ImpersonationBanner.vue'
  import AccountTabsBar from './components/account/AccountTabsBar.vue'
  import ClinicalSetupDialog from './components/account/ClinicalSetupDialog.vue'
  import { usePatientStore } from './components/composables/usePatientStore.js'
  import { AVAILABLE_LOCALES } from './messages.js'
  import { useDarkMode } from './composables/useDarkMode.js'
  import http from './http-common'
  import { runPendingTransfer } from '@local/migrate'
  import { showNotification } from './stores/notification'
  import { startLocalSession, refreshLicence } from '@local/boot'
  import { isMobileDevice } from './utils/device'

  const user = ref(null)
  const authLoading = ref(true)
  const autoSelectAttempted = ref(false)
  const route = useRoute()
  const { startRoleMonitoring, stopRoleMonitoring } = useAuth()
  const { t, locale } = useI18n()
  const patientStore = usePatientStore()
  
  // Dark mode (centralized: keeps localStorage, the `.dark` html class, and the
  // active Vuetify theme in sync — shared with the app-bar toggle).
  const { toggleDarkMode } = useDarkMode()

  // Expose toggle for dev testing via console: window.toggleDarkMode()
  if (typeof window !== 'undefined') {
    window.toggleDarkMode = toggleDarkMode
  }
  
  // NO PLAN-SELECTION GATE. This used to hold `requiresPlanSelection`, which
  // pushed any account owner with no `planType` to /plans and — via a watcher
  // on the route — shoved them back every time they tried to leave. It was
  // written when a subscription was required to use the app at all.
  //
  // FREE IS A PLAN NOW, so "no planType" is the normal state of a working
  // account, and the gate caught every new signup and held it on a page selling
  // the retired $45 / $35 plans. Removed 2026-08-27 along with /plans itself;
  // Retroact does not sell, praxio.net does. What a pharmacist may actually DO
  // is decided server-side by the entitlement rule (backend/middleware/
  // entitlement.js), not by a client-side redirect.

  // Settings pages get their top menu rendered by the shell (see template).
  const isAccountRoute = computed(() => route.path.startsWith('/account'))

  // Check if current route is an auth page that should be full-screen
  const isAuthPage = computed(() => {
    const authRoutes = ['/signin', '/signup', '/auth/magic-link']
    // Also check if path starts with /auth/ (for /auth/action and any other auth pages)
    return authRoutes.includes(route.path) || route.path.startsWith('/auth/')
  })

  // Quick entry is the one signed-in route with a phone layout, so it is also
  // the one that must NOT be wrapped in the shell below: that shell is a 1600px
  // container with a 56px rail drawer and an app bar sized for a desktop, and it
  // would frame a screen built for a 390px viewport in furniture the viewport
  // cannot fit. It renders itself instead (components/quickEntry).
  const isQuickEntry = computed(() => route.path === '/quick-entry')

  // The portal's hidden transfer frame. It renders nothing, and it must not be
  // wrapped in the shell: mounting an app bar, a drawer and a 1600px container
  // inside a 1x1 iframe costs a full app boot to draw a page no one can see.
  const isTransferFrame = computed(() => route.path === '/embed/deployment-transfer')

  // Every route that owns its full viewport.
  const isFullScreenPage = computed(
    () => isAuthPage.value || isQuickEntry.value || isTransferFrame.value
  )

  // Routes that render correctly below the shell's 800px floor.
  //
  //  - Quick entry is BUILT for a ~390px viewport.
  //  - The SSO landing renders nothing but a centred AlertPage (max-width 28rem,
  //    width 100%), which has never needed the floor. It is also the screen the
  //    phone hits FIRST on its way to quick entry, so leaving it out meant the
  //    clinician met a desktop-width splash before reaching a phone-width form.
  const isPhoneSafePage = computed(() => isQuickEntry.value || route.path.startsWith('/auth/sso'))

  // The shell has a hard floor of 800px because every clinical screen is a
  // multi-panel desktop layout: below that the panels overlap rather than
  // reflow, so the floor is a guardrail, not a preference.
  //
  // Applied to a phone-sized screen it does the opposite: quick entry's 520px
  // column centred itself inside an 800px box and half of it hung off the side
  // of the phone. The height floor stays either way — no phone is under 500px
  // tall.
  const appMinSize = computed(() =>
    isPhoneSafePage.value
      ? { minHeight: '500px' }
      : { minWidth: '800px', minHeight: '500px' }
  )
  
  
  
  /**
   * Say what the pending move did, including when it did nothing.
   *
   * THE SILENCE WAS THE BUG. This ran "silently by contract" on the reasoning
   * that a background check finding nothing to do should not interrupt anyone.
   * What that produced was a move which declined for a perfectly good reason --
   * no licence cached, nothing on this side to send, the far side already
   * emptied -- and looked identical to one that had never run at all. Records
   * stayed where they were and the only symptom was their absence somewhere
   * else, with nothing anywhere saying why.
   *
   * `already_transferred` and `no_pending` are the ordinary answers and stay
   * quiet. Everything else is either something the pharmacist can act on or
   * something whoever is debugging this needs to see, so it is said out loud.
   */
  const QUIET_REASONS = ['already_transferred', 'not_local_build']

  const reportPendingTransfer = (outcome) => {
    if (!outcome) return

    // Always in the console: this is the one place the whole chain reports
    // itself, and it costs nothing. Stripped from production builds by the
    // terser `drop_console` rule in vue.config.js.
    console.info('[retroact] pending transfer:', outcome)

    if (outcome.ran) {
      // A completed move replaced every record in this browser, so the list on
      // screen is a list of the wrong panel until it is refetched.
      patientStore.fetchPatients(true)
      showNotification(t('backup.transfer.success'), 'success', 4000)
      return
    }

    if (outcome.reason && !QUIET_REASONS.includes(outcome.reason)) {
      // The engine's own sentence where it wrote one -- a plan ceiling and a
      // blocked download are different problems with different fixes, and
      // collapsing them into one message is how someone ends up in support.
      const message = outcome.error?.message || t('backup.transfer.error')
      showNotification(message, 'warning', 8000)
    }
  }

  onMounted(() => {
    // Listen for auth state changes
    onAuthStateChange(async (firebaseUser) => {
      user.value = firebaseUser
      
      // For invited users, force token refresh to ensure custom claims are loaded
      if (firebaseUser && firebaseUser.uid.startsWith('user_')) {
        try {
          await firebaseUser.getIdToken(true); // Force refresh
          const idTokenResult = await firebaseUser.getIdTokenResult();
          
          // Verify custom claims are present
          if (!idTokenResult.claims.userId || !idTokenResult.claims.accountOwnerId) {
            console.warn('⚠️ Custom claims missing, refreshing again...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            await firebaseUser.getIdToken(true);
            const secondCheck = await firebaseUser.getIdTokenResult();
          }
        } catch (claimsError) {
          console.error('❌ Error loading custom claims:', claimsError);
        }
      }
      
      authLoading.value = false
      
      if (firebaseUser) {
        // Start role monitoring with a small delay
        setTimeout(() => {
          startRoleMonitoring();
        }, 500);

        // THE DEPLOYMENT MOVE, run where it can actually run.
        //
        // The pharmacist changes the deployment in the PORTAL, which cannot
        // move anything: both directions need this browser's IndexedDB, and
        // praxio.net is a different origin. The portal therefore changes the
        // setting and nothing else -- it does not open this app, and nobody is
        // sent anywhere.
        //
        // So the move happens here instead, silently, whenever this app is
        // running. It is a no-op unless the licence says the records belong on
        // the other side, and every refusal the engine already makes still
        // applies. In a cloud or demo build `@local` resolves to a stand-in
        // that answers "nothing pending" without touching anything.
        // THE LICENCE FIRST, ALWAYS. It is what carries the deployment the
        // portal just changed, and runPendingTransfer reads the CACHED copy --
        // so without this it asks a cache nobody has refreshed and finds no
        // direction to move in. That is not a quiet no-op: it is patient
        // records sitting on-premise after a switch to cloud, with nothing on
        // screen saying why.
        // NOT INSIDE THE TRANSFER FRAME. That route runs the move itself, on
        // the portal's schedule, and two concurrent runs would have both
        // reading this database while one of them replaces it. The frame is
        // the owner there; this handler stands down.
        if (!isTransferFrame.value) {
          startLocalSession(http)
            // THE SESSION STARTS ANYWHERE; THE MOVE DOES NOT. A pull writes the
            // whole panel into THIS browser's IndexedDB and empties the cloud
            // behind it, and a mobile browser is free to evict that store -- so
            // a phone or a tablet declines it and leaves the marker set. The
            // move then happens the next time this account opens the app on a
            // computer: deferred, not abandoned.
            //
            // The refusal is placed HERE rather than around the whole block on
            // purpose. startLocalSession mints the install id, asks for storage
            // persistence and refreshes the licence; skipping that would leave
            // an on-premise account running without one, which is a bigger
            // problem than the one this guard exists to prevent.
            //
            // See utils/device.js, and the matching refusal in the portal's
            // RetroactTransferRunner.vue. `reportPendingTransfer` ignores a
            // null outcome, so declining is silent.
            .then(() => (isMobileDevice() ? null : runPendingTransfer(http)))
            .then(reportPendingTransfer);
        }
      } else {
        // Stop monitoring when user signs out
        stopRoleMonitoring();
        autoSelectAttempted.value = false;
      }
    })

    // Coming BACK to this tab is the other moment a move can become pending:
    // the pharmacist flips the setting in the portal tab and returns to this
    // one. Cheap to check -- it reads a cached licence and returns immediately
    // when there is nothing to do.
    const handleFocus = () => {
      if (!user.value) return
      // The licence again, for the same reason as on sign-in -- coming back to
      // this tab is precisely the moment the setting has just changed in the
      // other one, so the cached answer is the stale one. `refreshLicence`
      // rather than the whole of startLocalSession: one POST, not three.
      // Same stand-down as on sign-in: the frame owns the move on its route.
      if (isTransferFrame.value) return
      refreshLicence(http)
        // Same split as on sign-in: the licence is still refreshed on a phone,
        // the move is not performed there.
        .then(() => (isMobileDevice() ? null : runPendingTransfer(http)))
        .then(reportPendingTransfer)
    }
    window.addEventListener('focus', handleFocus)
    
    // Listen for postMessage from Praxio (opener window)
    const handlePostMessage = (event) => {
      // In production, check event.origin for security
      // For localhost development, we accept messages from any origin
      
      if (event.data.type === 'praxio-language-change' && event.data.locale) {
        const newLang = event.data.locale
        // Whatever ships in src/locales/ — never a second hardcoded list.
        
        if (AVAILABLE_LOCALES.includes(newLang) && locale.value !== newLang) {
          locale.value = newLang
          localStorage.setItem('praxio_language', newLang)
        }
      }
    }
    
    window.addEventListener('message', handlePostMessage)
    
    // Watch for patients to be loaded and auto-select if needed
    watch(
      () => [patientStore.isEnriched.value, patientStore.activePatients.value, user.value],
      ([isEnriched, activePatients, currentUser]) => {
        if (isEnriched && activePatients?.length && currentUser && !autoSelectAttempted.value) {
          autoSelectAttempted.value = true;
          
          const currentSelectedId = patientStore.activeSelectedPatientId.value;
          const selectedPatientExists = currentSelectedId && activePatients.some(p => p.id === currentSelectedId);
          
          if (!selectedPatientExists) {
            const firstActive = activePatients[0];
            // Set localStorage directly first
            localStorage.setItem('inr_active_selected_patient_id', JSON.stringify(firstActive.id));
            localStorage.setItem('inr_selected_patient_id', JSON.stringify(firstActive.id));
            // Force window reload to refresh all components
            window.location.reload();
          } else {
          }
        }
      },
      { immediate: true }
    );
    
    // Cleanup
    onUnmounted(() => {
      window.removeEventListener('message', handlePostMessage)
      window.removeEventListener('focus', handleFocus)
      stopRoleMonitoring()
    })
  })
  
</script>
<template>
  <!--
    NOTE: no `id="app"` here. `public/index.html` already declares
    `<div id="app">` as the mount point, so putting the same id on <v-app>
    rendered TWO elements with id="app" in the live DOM — invalid HTML, and it
    makes document.getElementById('#app') and every `#app` selector ambiguous
    (it is what made the Playwright clinical smoke specs unable to address the
    app root at all). Nothing styles or queries this id; the mount point keeps it.
  -->
  <v-app :style="appMinSize">
    <!-- Impersonation Banner (shown at top when impersonating) -->
    <ImpersonationBanner />
    
    <!-- Show loading spinner while auth is initializing -->
    <div v-if="authLoading" class="d-flex justify-center align-center" style="height: 100vh;">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </div>
    
    <!-- Main app content -->
    <div v-else>
      <!-- Full-screen pages (no layout): auth, and quick entry. -->
      <div v-if="isFullScreenPage">
        <router-view />
      </div>
      
      <!-- Regular app pages (with layout) -->
      <!-- bg-surface: the layout container is capped at max-width:1600px and left-aligned,
           so fill the gap to its right with the same surface color the content cards use
           (instead of falling through to the darker v-app background). -->
      <div v-else class="bg-surface app-shell">
        <appBar />
        <!-- Settings top menu. It lives here, outside the capped container, so
             its bottom border runs the full width of the window like the app
             bar's does; the container below stays capped. -->
        <AccountTabsBar v-if="isAccountRoute" />
        <v-container class="pa-0 pr-12" style="width:100%;flex:1;min-height:0;max-width:1600px;margin-left:0;margin-right:auto;" >
            <mainDrawer/>         
            <v-main style="padding-left:56px;padding-top:0;width:calc(100%);height:100%;">
              <v-card style="width:100%;height:100%;display:flex;flex-direction:column;" rounded="0" elevation="0" color="transparent">
              <v-sheet
              ref="mainFrame" 
                elevation="0"
                rounded="0"
                style="overflow:hidden;width:100%;flex:1;display:flex;flex-direction:column;"
                color="transparent"
              >
                <router-view style="width:100%;flex:1;overflow:hidden;"/>
              </v-sheet>
              </v-card>
            </v-main>
        </v-container>
      </div>
    </div>

    <!-- First run: the account owner names the protocol every dose will be
         checked against, before anything is recorded against a default nobody
         chose. Mounted in the shell rather than routed to, so it is presented
         DIRECTLY over whatever the app was about to show — a redirect would
         have to land somewhere else first, and on a brand-new account that
         somewhere is itself a gate ("add your first patient").

         It opens itself: the component asks the server whether this caller is
         held, and stays invisible for everyone else, which is almost every
         caller almost every time. Not rendered on the auth pages, where there
         is no signed-in account to configure yet.

         Not on quick entry either: that dialog is a desktop form, and holding a
         phone-sized screen behind a gate it cannot render would strand the
         clinician with a value in hand and nowhere to put it. The account is
         still held — the gate is server-side and re-asserts itself the moment
         they open Retroact at the workstation, which is where the protocol is
         chosen and where the dose gets set anyway. -->
    <ClinicalSetupDialog v-if="user && !isFullScreenPage" />

    <!-- Global Notification Snackbar -->
    <GlobalNotification />
  </v-app>
</template>
<script>

export default {
  name: "app",
  data() {
    return {
      rail: true,
      drawer: true,
      elKey:0
    };
  },

  methods: {
    title() {
      switch(this.$route.name){
        case 'patients':
          return 'lists.' 
        case 'drugsList':
          return 'lists.'  
        case 'addPatient':
          return 'forms.' 
        case 'addDrug':
          return 'forms.'                
      }
    }  
  }   
};
</script>

<style>
/* The fixed app bar is out of flow, so the shell reserves its height once and
   lays out everything below it in a column (top menu, then the page). */
.app-shell {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  padding-top: 56px;
}

/* Smooth light/dark theme transition for surfaces, text and borders.
   Scoped to color properties so it never animates layout. */
.v-application,
.v-card,
.v-sheet,
.v-navigation-drawer,
.v-toolbar,
.v-list,
.v-list-item,
.v-table,
.v-field__overlay {
  transition: background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease;
}

/* Global style to prevent text transformation on all Vuetify buttons */
.v-btn {
  text-transform: none !important;
}

/* Global border radius and borders for all v-alerts */
.v-alert {
  border-radius: 8px !important;
  border: 1px solid transparent !important;
}

/* Darker borders for each alert type */
.v-alert--variant-tonal.text-success,
.v-alert[class*="bg-success"] {
  border-color: #c8e6c9 !important; /* Extra light green */
}

.v-alert--variant-tonal.text-error,
.v-alert[class*="bg-error"] {
  border-color: #ffcdd2 !important; /* Extra light red */
}

.v-alert--variant-tonal.text-warning,
.v-alert[class*="bg-warning"] {
  border-color: #ffe0b2 !important; /* Extra light orange */
}

.v-alert--variant-tonal.text-info,
.v-alert[class*="bg-info"] {
  border-color: #bbdefb !important; /* Extra light blue */
}

/* Disable page zooming */
html, body {
  touch-action: manipulation;
  -ms-touch-action: manipulation;
  -webkit-text-size-adjust: 100%;
  -moz-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  text-size-adjust: 100%;
}
</style>